import { Injectable, Injector } from '@angular/core';
import { OAuthService, AuthConfig, JwksValidationHandler } from 'angular-oauth2-oidc';
import { JwtHelperService } from '@auth0/angular-jwt';


@Injectable()
export class OidcAuthService {

    constructor(
        private injector: Injector
    ) {

    }

    queryToObject(str: string): any {
        return (str || '?')
            .slice(1)
            .split('&')
            .map(p => p.split('='))
            .reduce((obj, pair) => {
                if (pair[0]) {
                    const [key, value] = pair.map(decodeURIComponent);
                    return ({ ...obj, [key]: value });
                } else {
                    return obj;
                }
            }, {});
    }

    async authenticateInPopup(issuer: string, clientId: string): Promise<any> {

        const redirectUrl = window.location.origin;
        const loginUrl = await this.getLoginUrl(issuer, clientId, redirectUrl);
        // now open a popup window with the loginUrl, and get the full url after it redirects
        const resultUrl = await this.openPopupWindowAndCloseOnUrlChange(loginUrl, redirectUrl);

        return this.decodeIdTokenFromAuthUrl(resultUrl);
    }

    decodeUrlParameters<T>(authUrl: string): T {
        // decode the hash or query parameters from the returned URL
        const url = new URL(authUrl);
        return {
            ...this.queryToObject(url.search),
            ...this.queryToObject(url.hash),
        };
    }

    decodeIdTokenFromAuthUrl<T>(authUrl: string): T {
        const params = this.decodeUrlParameters<{ id_token: string }>(authUrl);
        if (params.id_token) {
            const helper = new JwtHelperService();
            const data = helper.decodeToken(params.id_token);
            return data;
        } else {
            throw new Error('No id_token was returned from the authentication issues');
        }
    }


    async getLoginUrl(issuer: string, clientId: string, redirectUrl: string): Promise<string> {
        const oauthService = await this.getOAuthService(issuer, clientId, redirectUrl);
        return await this.createLoginUrl(oauthService, '', '', redirectUrl);
    }

    async getOAuthService(issuer: string, clientId: string, redirectUrl: string): Promise<OAuthService> {
        const oauthService = this.injector.get(OAuthService);
        const authConfig: AuthConfig = {
            // Url of the Identity Provider
            issuer: issuer,
            // URL of the SPA to redirect the user to after login
            redirectUri: redirectUrl,
            // The SPA's id. The SPA is registerd with this id at the auth-server
            clientId: clientId,
            // set the scope for the permissions the client should request
            // The first three are defined by OIDC. The 4th is a usecase-specific one
            scope: 'openid profile email',
            strictDiscoveryDocumentValidation: false,
            /** some issuers don't include the https:// part and this fucks out */
            skipIssuerCheck: true,
            // showDebugInformation: true,
        };

        oauthService.configure(authConfig);
        oauthService.tokenValidationHandler = new JwksValidationHandler();
        await oauthService.loadDiscoveryDocument();
        return oauthService;
    }

    /** Opens a popup window with the specified url and waits until the URL changes to a specific 
     *  URL before closing the window and returning the full URL to which it was changed.
     */
    async openPopupWindowAndCloseOnUrlChange(url: string, closeUrl: string): Promise<string> {

        const win = window.open(url, 'windowname1', 'width=800, height=600');
        if (window.focus) {
            win.focus();
        }

        return new Promise<string>((resolve) => {
            const pollTimer = window.setInterval(function () {
                try {
                    if (win.document.URL.indexOf(closeUrl) !== -1) {
                        clearInterval(pollTimer);
                        const result = win.document.URL;
                        win.close();
                        resolve(result);
                    }
                } catch (e) {
                    // we're going to get a whole bunch of "Blocked a frame with origin "x" from accessing a cross-origin frame" here 
                    // until the auth service redirects us back to our own domain, just ignore it.
                }
            }, 100);
        });

    }

    /** This method was copied from the OAuthService code as there is a bug which prevents us from intercepting the location.href call in the 
     * service. When https://github.com/manfredsteyer/angular-oauth2-oidc/pull/369 is merged this can likely be removed.
     */
    createLoginUrl(oauthService: OAuthService, state = '', loginHint = '', redirectUri = '', noPrompt = false, params = {}): Promise<string> {
        let responseType = 'id_token token';

        return oauthService.createAndSaveNonce().then((nonce) => {
            if (state) {
                state = nonce + oauthService.nonceStateSeparator + state;
            } else {
                state = nonce;
            }
            if (!oauthService.requestAccessToken && !oauthService.oidc) {
                throw new Error('Either requestAccessToken or oidc or both must be true');
            }
            if (oauthService.responseType) {
                responseType = oauthService.responseType;
            } else {
                if (oauthService.oidc && oauthService.requestAccessToken) {
                    responseType = 'id_token token';
                } else if (oauthService.oidc && !oauthService.requestAccessToken) {
                    responseType = 'id_token';
                } else {
                    responseType = 'token';
                }
            }
            const seperationChar = oauthService.loginUrl.indexOf('?') > -1 ? '&' : '?';
            let scope = oauthService.scope;
            if (oauthService.oidc && !scope.match(/(^|\s)openid($|\s)/)) {
                scope = 'openid ' + scope;
            }
            let url = oauthService.loginUrl +
                seperationChar +
                'response_type=' + encodeURIComponent(responseType) +
                '&client_id=' + encodeURIComponent(oauthService.clientId) +
                '&state=' + encodeURIComponent(state) +
                '&redirect_uri=' + encodeURIComponent(redirectUri) +
                '&scope=' + encodeURIComponent(scope);

            if (loginHint) {
                url += '&login_hint=' + encodeURIComponent(loginHint);
            }
            if (oauthService.resource) {
                url += '&resource=' + encodeURIComponent(oauthService.resource);
            }
            if (oauthService.oidc) {
                url += '&nonce=' + encodeURIComponent(nonce);
            }
            if (noPrompt) {
                url += '&prompt=none';
            }
            for (const key of Object.keys(params)) {
                url += '&' + encodeURIComponent(key) + '=' + encodeURIComponent(params[key]);
            }
            if (oauthService.customQueryParams) {
                for (const key of Object.getOwnPropertyNames(oauthService.customQueryParams)) {
                    url += '&' + key + '=' + encodeURIComponent(oauthService.customQueryParams[key]);
                }
            }
            return url;
        });
    }



}
